import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TableContainer,
  Paper,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import ListSearchField from "../listSearchField/ListSearchField";
import {
  getAcquirerList,
  getIsoList,
  getMerchantList,
} from "./AdminFields.thunk";
import AuthContext from "../../context/AuthContext";
import { useContext } from "react";
import DataTable from "../dataTable/DataTable";
import { useEffectOnce } from "../../utils/effects";
import CheckCircleIcon from "@mui/icons-material/RadioButtonUnchecked";
import { GridContainer, GridItem } from "./AdminFields.styles";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    minWidth: "600px",
    maxWidth: "800px",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  "& .MuiTableHead-root": {
    backgroundColor: theme.palette.grey[100],
  },
}));

const AdminFieldsModal = ({
  open,
  onClose,
  onSelectAcquirer = () => {},
  onSelectISO = () => {},
  onSelectMerchant = () => {},
  type,
}) => {
  const titles = {
    acquirer: "Acquirer",
    iso: "ISO",
    merchant: "Merchant",
  };
  const dispatch = useDispatch();
  const { token, acquirerData: acquirerDataContext } = useContext(AuthContext);
  const [searchValue, setSearchValue] = useState("");
  const {
    acquirerId,
    isoID,
    acquirerData,
    acquirerPage,
    acquirerPageSize,
    acquirerTotalRecords,
    acquirerStatus,
    isoData,
    isoPage,
    isoPageSize,
    isoTotalRecords,
    isoStatus,
    merchantData,
    merchantPage,
    merchantPageSize,
    merchantTotalRecords,
    merchantStatus,
  } = useSelector((state) => state.adminFields);

  const loading =
    acquirerStatus === "loading" ||
    isoStatus === "loading" ||
    merchantStatus === "loading";

  useEffectOnce(() => {
    if (type === "acquirer")
      dispatch(
        getAcquirerList({
          token,
          page: 1,
          page_size: acquirerPageSize,
        }),
      );
    if (type === "iso")
      dispatch(
        getIsoList({
          token,
          acquirerData: {
            acquirerId: acquirerId,
            acquirerName: acquirerDataContext?.acquirerName,
          },
          page: 1,
          page_size: isoPageSize,
        }),
      );
    if (type === "merchant")
      dispatch(
        getMerchantList({
          token,
          acquirerId: acquirerId,
          isoID,
          acquirerName: acquirerDataContext?.acquirerName,
          page: 1,
          page_size: merchantPageSize,
        }),
      );
    setSearchValue("");
  });

  const acquirerColumns = [
    { id: "acquirer_id", label: "Acquirer ID" },
    { id: "acquirer_name", label: "Acquirer Name" },
    { id: "currency_alpha3_code", label: "Currency Code" },
    {
      id: "action",
      label: "Action",
      actions: [
        {
          icon: <CheckCircleIcon color="primary" />,
          label: "Select",
          onClick: (row) => handleSelectAcquirer(row),
        },
      ],
    },
  ];

  const isoColumns = [
    { id: "isoID", label: "ISO ID" },
    { id: "isoName", label: "ISO Name" },
    { id: "isoCurrencyCode", label: "Currency Code" },
    {
      id: "action",
      label: "Action",
      actions: [
        {
          icon: <CheckCircleIcon color="primary" />,
          label: "Select",
          onClick: (row) => handleSelectISO(row),
        },
      ],
    },
  ];

  const merchantColumns = [
    { id: "merchantID", label: "Merchant ID" },
    { id: "merchantName", label: "Merchant Name" },
    {
      id: "action",
      label: "Action",
      actions: [
        {
          icon: <CheckCircleIcon color="primary" />,
          label: "Select",
          onClick: (row) => handleSelectMerchant(row),
        },
      ],
    },
  ];

  const columns = {
    acquirer: acquirerColumns,
    iso: isoColumns,
    merchant: merchantColumns,
  };

  const pages = {
    acquirer: acquirerPage,
    iso: isoPage,
    merchant: merchantPage,
  };

  const rowsPerPages = {
    acquirer: acquirerPageSize,
    iso: isoPageSize,
    merchant: merchantPageSize,
  };

  const totals = {
    acquirer: acquirerTotalRecords,
    iso: isoTotalRecords,
    merchant: merchantTotalRecords,
  };

  const searchQuery =
    typeof searchValue === "string" && searchValue.trim()
      ? { search: searchValue.trim() }
      : {};

  const handlePageChange = (event, newPage) => {
    if (type === "acquirer") {
      dispatch(
        getAcquirerList({
          token,
          page: newPage + 1,
          page_size: acquirerPageSize,
          ...searchQuery,
        }),
      );
    } else if (type === "iso") {
      dispatch(
        getIsoList({
          token,
          acquirerData: {
            acquirerId: acquirerId,
            acquirerName: acquirerDataContext?.acquirerName,
          },
          page: newPage + 1,
          page_size: isoPageSize,
          ...searchQuery,
        }),
      );
    } else if (type === "merchant") {
      dispatch(
        getMerchantList({
          token,
          acquirerId: acquirerId,
          isoID,
          acquirerName: acquirerDataContext?.acquirerName,
          page: newPage + 1,
          page_size: merchantPageSize,
          ...searchQuery,
        }),
      );
    }
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = +event.target.value;
    if (type === "acquirer") {
      dispatch(
        getAcquirerList({
          token,
          page: 1,
          page_size: newPageSize,
          ...searchQuery,
        }),
      );
    } else if (type === "iso") {
      dispatch(
        getIsoList({
          token,
          acquirerData: {
            acquirerId: acquirerId,
            acquirerName: acquirerDataContext?.acquirerName,
          },
          page: 1,
          page_size: newPageSize,
          ...searchQuery,
        }),
      );
    } else if (type === "merchant") {
      dispatch(
        getMerchantList({
          token,
          acquirerId: acquirerId,
          isoID,
          acquirerName: acquirerDataContext?.acquirerName,
          page: 1,
          page_size: newPageSize,
          ...searchQuery,
        }),
      );
    }
  };

  const handleSelectAcquirer = (acquirer) => {
    onSelectAcquirer(acquirer);
    onClose();
  };

  const handleSelectISO = (iso) => {
    onSelectISO(iso);
    onClose();
  };

  const handleSelectMerchant = (merchant) => {
    onSelectMerchant(merchant);
    onClose();
  };

  const handleSearch = () => {
    if (type === "acquirer") {
      dispatch(
        getAcquirerList({
          token,
          page: 1,
          page_size: acquirerPageSize,
          ...searchQuery,
        }),
      );
    } else if (type === "iso") {
      dispatch(
        getIsoList({
          token,
          acquirerData: {
            acquirerId: acquirerId,
            acquirerName: acquirerDataContext?.acquirerName,
          },
          page: 1,
          page_size: isoPageSize,
          ...searchQuery,
        }),
      );
    } else if (type === "merchant") {
      dispatch(
        getMerchantList({
          token,
          acquirerId: acquirerId,
          isoID,
          acquirerName: acquirerDataContext?.acquirerName,
          page: 1,
          page_size: merchantPageSize,
          ...searchQuery,
        }),
      );
    }
  };

  if (!type) return null;
  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Select {titles[type]}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <GridContainer>
          <GridItem xs={12} md={6} padding="15px 0 0">
            <ListSearchField
              fullWidth
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onSearch={handleSearch}
            />
          </GridItem>
          <GridItem xs={12} md={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose a {titles[type]} from the list below:
            </Typography>
            <StyledTableContainer component={Paper}>
              <DataTable
                loading={loading}
                columns={columns[type]}
                rows={
                  type === "acquirer"
                    ? acquirerData
                    : type === "iso"
                      ? isoData
                      : merchantData
                }
                // page={type === "acquirer" ? acquirerPage - 1 : isoPage - 1}
                page={pages[type] - 1}
                // rowsPerPage={
                //   type === "acquirer" ? acquirerPageSize : isoPageSize
                // }
                rowsPerPage={rowsPerPages[type]}
                // count={
                //   type === "acquirer" ? acquirerTotalRecords : isoTotalRecords
                // }
                count={totals[type]}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </StyledTableContainer>
          </GridItem>
        </GridContainer>
      </DialogContent>
    </StyledDialog>
  );
};

export default AdminFieldsModal;
